import { z } from 'zod';
export const reelRequestSchema = z.object({
  kind: z.enum(['storyboard', 'video']),
  seconds: z.union([z.literal(15), z.literal(30)]).default(15),
  // Grounded by default: the host's own photos are the point, so AI treatment is opt-in.
  fidelity: z.enum(['original', 'balanced', 'enhanced']).default('original'),
  revision: z.string().trim().max(2000).default(''),
  parentId: z.string().uuid().optional(),
  approvedBoardId: z.string().uuid().optional(),
  /** One-based scene numbers to draw again on the parent board; with none, the note decides. */
  scenes: z.array(z.number().int().min(1).max(12)).min(1).max(12).optional(),
});
export type ReelRequest = z.infer<typeof reelRequestSchema>;
export type ReelShot = {
  id: string;
  title: string;
  image: string;
  seconds: number;
  caption: string;
  motion: string;
  notes: string;
  source?: number | null;
  kind?: string;
  /** How many times the scene was drawn before the judge passed it (or gave up). */
  attempts?: number;
  /** The judge's last word on the frame that was kept. */
  quality?: { ok: boolean; problems: string[]; fix: string } | null;
  /** 'photo' when no attempt passed and the host's own photo stands in. */
  fallback?: 'photo';
};
export type ReelResult = {
  id: string;
  scope?: string;
  status: string;
  progress?: string;
  error?: string;
  shots?: ReelShot[];
  story?: { title: string; summary: string };
  url?: string;
  changedSceneCount?: number;
  caption?: string;
  hashtags?: string[];
  /** ffprobe's own reading of the assembled file; the source of truth for its length. */
  media?: { format?: { duration?: string } };
};
export type ReelJobDto = {
  id: string;
  kind: 'storyboard' | 'video';
  state: 'pending' | 'running' | 'complete' | 'failed';
  input: ReelRequest;
  result: ReelResult | null;
  error: string | null;
  credits: number;
  createdAt: string;
  versionId: string | null;
};
/** Seedance 2 on PiAPI, 720p: what a billed second costs. */
const SEEDANCE2_USD_PER_SECOND = 0.106;

/**
 * How many scenes a reel of this length gets: about four seconds each, between three and twelve.
 * The content API plans with the same rule (content-api src/domain/reel.ts `sceneCount`); the quote
 * must price the scenes the host actually gets.
 */
export function sceneCount(seconds: number): number {
  return Math.min(12, Math.max(3, Math.round(seconds / 4)));
}

/**
 * Credits for a storyboard (two a scene) or for filming it. Reels film on Seedance 2, one call per
 * shot, each billed its whole seconds and never under 4; the board's seconds are split over its
 * scenes as the content API splits them. Six credits a dollar.
 */
export function reelQuote(kind: 'storyboard' | 'video', seconds: number) {
  const scenes = sceneCount(seconds);
  const billed = Array.from({ length: scenes }, (_, i) =>
    Math.max(4, Math.floor(seconds / scenes) + (i < seconds % scenes ? 1 : 0)),
  ).reduce((n, s) => n + s, 0);
  const providerEstimateUsd = billed * SEEDANCE2_USD_PER_SECOND;
  return {
    credits: kind === 'storyboard' ? scenes * 2 : Math.ceil(providerEstimateUsd * 6),
    providerEstimateUsd: kind === 'video' ? providerEstimateUsd : null,
  };
}

/**
 * What a whole reel costs from nothing: its storyboard and its filming, as the API spends them.
 * Every screen that quotes a reel's price (signup, the credits sheet, the trial pack) reads this.
 */
export function reelPrice(seconds: number) {
  const storyboard = reelQuote('storyboard', seconds).credits;
  const filming = reelQuote('video', seconds).credits;
  return { storyboard, filming, total: storyboard + filming };
}
